import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	IServerError,
	IDeviceHardwareIdValidationError,
	ICreateDevice,
	IDeviceBankSettingForCreate,
	IRestaurantBankSettings,
	TTlsCertificateLevel,
} from 'types';

export type TCreateDeviceSelectedItem = string | null;
export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'tlsFileSizeExceeded'
	| 'tlsFileTypeError';

export interface ICreateDeviceState {
	hardwareId: string;
	fetchedCustomers: IItemForGetShortInfo[];
	IDSelectedCustomer: TCreateDeviceSelectedItem;
	fetchedRestaurants: IItemForGetShortInfo[];
	IDSelectedRestaurant: TCreateDeviceSelectedItem;
	bankSettings: IDeviceBankSettingForCreate[];
	fetchedRestaurantBankSettings: IRestaurantBankSettings[];
	tlsCertificateLevel: Nullable<TTlsCertificateLevel>;
	tlsCertificateName: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	tlsCertificateFileHash: Nullable<string>;
	dataForSubmit: ICreateDevice | null;
	typeOfModalWindow: TTypeOfModalWindow;
	errorResponseData: IServerError<IDeviceHardwareIdValidationError> | null;
}

export const createDeviceInitialState: ICreateDeviceState = {
	hardwareId: '',
	fetchedCustomers: [],
	IDSelectedCustomer: null,
	fetchedRestaurants: [],
	IDSelectedRestaurant: null,
	bankSettings: [],
	fetchedRestaurantBankSettings: [],
	tlsCertificateLevel: null,
	tlsCertificateName: null,
	tlsCertificateFileId: '',
	tlsCertificateFileHash: null,
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface ICreateDeviceContext extends ICreateDeviceState {
	changeHardwareId(value: string): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: TCreateDeviceSelectedItem): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setIDRestaurant(value: TCreateDeviceSelectedItem): void;
	addBankSettingsBlock(value: IDeviceBankSettingForCreate): void;
	deleteBankSettingsBlock(value: string): void;
	changeRestaurantBankSettings(settingsId: string, value: string | null): void;
	changeBankSettingsTerminalId(settingsId: string, value: string): void;
	setFetchedRestaurantBankSettings(value: IRestaurantBankSettings[]): void;
	changeBankSettingsM4Login(settingsId: string, value: string): void;
	changeBankSettingsM4Password(settingsId: string, value: string): void;
	changeBankSettingsToucanLogin(settingsId: string, value: string): void;
	changeBankSettingsToucanPassword(settingsId: string, value: string): void;
	changeTlsCertificateName(value: string): void;
	setTlsCertificateFile({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }): void;
	setDataForSubmit(value: ICreateDevice | null): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	showErrorModalWithInfo(value: IServerError<IDeviceHardwareIdValidationError> | null): void;
	resetFields(): void;
}

export const CreateDeviceContext = createContext<ICreateDeviceContext>({
	changeHardwareId: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDRestaurant: () => undefined,
	addBankSettingsBlock: () => undefined,
	deleteBankSettingsBlock: () => undefined,
	changeRestaurantBankSettings: () => undefined,
	changeBankSettingsTerminalId: () => undefined,
	setFetchedRestaurantBankSettings: () => undefined,
	changeBankSettingsM4Login: () => undefined,
	changeBankSettingsM4Password: () => undefined,
	changeBankSettingsToucanLogin: () => undefined,
	changeBankSettingsToucanPassword: () => undefined,
	changeTlsCertificateName: () => undefined,
	setTlsCertificateFile: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...createDeviceInitialState,
});
